import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, HeartHandshake, Clock, ChevronRight, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoupleExercisesPage() {
  const navigate = useNavigate();
  const { hasAccess, getAuthHeader } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExercise, setSelectedExercise] = useState(null);

  useEffect(() => {
    if (hasAccess) {
      fetchExercises();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const fetchExercises = async () => {
    try {
      const res = await axios.get(`${API}/couple-exercises`, { headers: getAuthHeader() });
      setExercises(res.data);
    } catch (error) {
      console.error('Error fetching exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="couple-exercises-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Parövningar</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till parövningar.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  if (selectedExercise) {
    return (
      <div className="min-h-screen bg-background" data-testid="couple-exercises-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedExercise(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Parövning</h1>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-purple-50 rounded-2xl p-8">
            <div className="flex items-center gap-2 text-purple-600 text-sm mb-3">
              <Clock className="w-4 h-4" />
              {selectedExercise.time}
            </div>
            <h2 className="font-heading text-2xl font-semibold mb-2">{selectedExercise.title}</h2>
            <p className="text-muted-foreground">{selectedExercise.description}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            <h3 className="font-semibold">Så gör ni</h3>
            <ol className="space-y-3">
              {selectedExercise.instructions.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-muted-foreground">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
            <p className="text-amber-800 text-sm">
              💡 <strong>Tips:</strong> {selectedExercise.tips}
            </p>
          </div>

          {/* Contact */}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="couple-exercises-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Parövningar</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-3">
          <HeartHandshake className="w-12 h-12 mx-auto text-purple-500" />
          <h2 className="font-heading text-2xl font-semibold">Parövningar</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Praktiska övningar för att stärka er relation, kommunicera bättre och ha roligt tillsammans.
          </p>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-24 bg-secondary/50 rounded-2xl animate-pulse" />)}
          </div>
        ) : (
          <div className="space-y-3">
            {exercises.map((exercise) => (
              <button
                key={exercise.id}
                onClick={() => setSelectedExercise(exercise)}
                className="w-full bg-white p-5 rounded-2xl border border-border/30 text-left hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center">
                    <HeartHandshake className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">{exercise.title}</h3>
                    <p className="text-sm text-muted-foreground">{exercise.description}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-purple-600">
                      <Clock className="w-3 h-3" />
                      {exercise.time}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Tips */}
        <div className="bg-purple-50 rounded-2xl p-4 border border-purple-100">
          <p className="text-purple-800 text-sm">
            💜 <strong>Tips:</strong> Gör en övning i veckan och diskutera efteråt. Små insatser ger stora resultat över tid.
          </p>
        </div>

        {/* Contact */}
      </main>
    </div>
  );
}
